import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Stack,
  Badge
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as BugIcon,
  Refresh as RefreshIcon,
  DeleteSweep as FlushIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import api from '../../services/api';

interface ErrorLog {
  id: number;
  error_level: string;
  error_type: 'critical' | 'warning' | 'notice' | 'deprecated';
  message: string;
  file: string | null;
  line: number | null;
  user_id: number | null;
  user_name: string | null;
  customer_id: number | null;
  customer_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  request_method: string | null;
  request_uri: string | null;
  stack_trace: string | null;
  context: any;
  created_at: string;
}

interface ErrorStatistics {
  error_type: string;
  count: number;
  last_occurrence: string;
}

const ErrorLogs: React.FC = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [statistics, setStatistics] = useState<ErrorStatistics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  
  // Filters
  const [errorType, setErrorType] = useState<string>('');
  const [errorLevel, setErrorLevel] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Dialogs
  const [flushDialogOpen, setFlushDialogOpen] = useState(false);
  const [daysToKeep, setDaysToKeep] = useState(30);

  const fetchErrorLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        ...(errorType && { error_type: errorType }),
        ...(errorLevel && { error_level: errorLevel }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      });
      
      const response = await api.get(`/endpoints/error-logs?${params}`);
      
      if (response.data.success) {
        setErrors(response.data.data.errors);
        setStatistics(response.data.data.statistics);
        setTotalCount(response.data.data.pagination.total);
      }
    } catch (err: any) {
      console.error('Error fetching error logs:', err);
      setError(err.response?.data?.message || 'Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, errorType, errorLevel, dateFrom, dateTo]);

  useEffect(() => {
    fetchErrorLogs();
  }, [fetchErrorLogs]);

  const handleFlushLogs = async () => {
    try {
      const response = await api.delete('/endpoints/error-logs', {
        params: { days_to_keep: daysToKeep }
      });
      
      if (response.data.success) {
        setFlushDialogOpen(false);
        fetchErrorLogs(); // Refresh the list
        // Show success message
        alert(`Successfully deleted ${response.data.data.deleted_rows} error logs`);
      }
    } catch (err: any) {
      console.error('Error flushing logs:', err);
      alert(err.response?.data?.message || 'Failed to flush logs');
    }
  };

  const toggleRowExpansion = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getErrorIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'notice':
        return <InfoIcon color="info" />;
      case 'deprecated':
        return <BugIcon color="disabled" />;
      default:
        return <InfoIcon />;
    }
  };

  const getErrorColor = (type: string): any => {
    switch (type) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      case 'notice':
        return 'info';
      case 'deprecated':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchErrorLogs();
  };

  const handleClearFilters = () => {
    setErrorType('');
    setErrorLevel('');
    setDateFrom(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
    setDateTo(format(new Date(), 'yyyy-MM-dd'));
    setSearchTerm('');
    setPage(0);
  };

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statistics.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.error_type}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      {stat.error_type.charAt(0).toUpperCase() + stat.error_type.slice(1)} Errors
                    </Typography>
                    <Typography variant="h4">
                      {stat.count}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Last: {format(new Date(stat.last_occurrence), 'HH:mm:ss')}
                    </Typography>
                  </Box>
                  {getErrorIcon(stat.error_type)}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Error Type</InputLabel>
                <Select
                  value={errorType}
                  onChange={(e) => setErrorType(e.target.value)}
                  label="Error Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="notice">Notice</MenuItem>
                  <MenuItem value="deprecated">Deprecated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Error Level"
                value={errorLevel}
                onChange={(e) => setErrorLevel(e.target.value)}
                fullWidth
                size="small"
                placeholder="E_ERROR, E_WARNING..."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="From Date"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="To Date"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                >
                  Clear
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setFlushDialogOpen(true)}
                  startIcon={<FlushIcon />}
                >
                  Flush Old Logs
                </Button>
                <IconButton onClick={fetchErrorLogs}>
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Logs Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Error Logs</Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}></TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errors.map((errorLog) => (
                      <React.Fragment key={errorLog.id}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpansion(errorLog.id)}
                            >
                              {expandedRows.has(errorLog.id) ? 
                                <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getErrorIcon(errorLog.error_type)}
                              label={errorLog.error_type}
                              size="small"
                              color={getErrorColor(errorLog.error_type)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {errorLog.error_level}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={errorLog.message}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                {errorLog.message}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {errorLog.file && (
                              <Tooltip title={`Line ${errorLog.line}`}>
                                <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                                  {errorLog.file.split('/').pop()}:{errorLog.line}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {errorLog.user_name || '-'}
                          </TableCell>
                          <TableCell>
                            {format(new Date(errorLog.created_at), 'MMM dd HH:mm:ss')}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpansion(errorLog.id)}
                              >
                                <CodeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, px: 0 }}>
                            <Collapse in={expandedRows.has(errorLog.id)} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Full Message:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {errorLog.message}
                                    </Typography>
                                  </Grid>
                                  
                                  {errorLog.file && (
                                    <Grid item xs={12}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Location:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {errorLog.file}:{errorLog.line}
                                      </Typography>
                                    </Grid>
                                  )}
                                  
                                  {errorLog.request_uri && (
                                    <Grid item xs={12}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Request:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {errorLog.request_method} {errorLog.request_uri}
                                      </Typography>
                                    </Grid>
                                  )}
                                  
                                  {errorLog.stack_trace && (
                                    <Grid item xs={12}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Stack Trace:
                                      </Typography>
                                      <Paper sx={{ p: 1, bgcolor: 'grey.900', maxHeight: 300, overflow: 'auto' }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontFamily: 'monospace',
                                            color: 'grey.100',
                                            whiteSpace: 'pre-wrap',
                                            fontSize: '0.8rem'
                                          }}
                                        >
                                          {errorLog.stack_trace}
                                        </Typography>
                                      </Paper>
                                    </Grid>
                                  )}
                                  
                                  {errorLog.context && Object.keys(errorLog.context).length > 0 && (
                                    <Grid item xs={12}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Context:
                                      </Typography>
                                      <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                        >
                                          {JSON.stringify(errorLog.context, null, 2)}
                                        </Typography>
                                      </Paper>
                                    </Grid>
                                  )}
                                  
                                  <Grid item xs={12}>
                                    <Stack direction="row" spacing={2}>
                                      <Typography variant="caption">
                                        <strong>IP:</strong> {errorLog.ip_address || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption">
                                        <strong>Customer:</strong> {errorLog.customer_name || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption">
                                        <strong>User Agent:</strong> {errorLog.user_agent?.substring(0, 50) || 'N/A'}...
                                      </Typography>
                                    </Stack>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Flush Dialog */}
      <Dialog open={flushDialogOpen} onClose={() => setFlushDialogOpen(false)}>
        <DialogTitle>Flush Old Error Logs</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will permanently delete error logs older than the specified number of days.
          </Typography>
          <TextField
            label="Days to Keep"
            type="number"
            value={daysToKeep}
            onChange={(e) => setDaysToKeep(parseInt(e.target.value) || 30)}
            fullWidth
            margin="normal"
            InputProps={{ inputProps: { min: 1, max: 365 } }}
          />
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. Error logs older than {daysToKeep} days will be permanently deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlushDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleFlushLogs} color="error" variant="contained">
            Flush Logs
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErrorLogs;